/**
 * UnauthorizedPage Component
 *
 * Displayed when user tries to access a route they don't have permission for.
 *
 * WHEN THIS PAGE SHOWS:
 * ────────────────────────────────────────────────────────────────
 * 1. Role-Based Protection:
 *    - User is authenticated but lacks required role
 *    - Example: customer trying to access /admin/ab-tests
 *    - Requires: super_admin or staff
 *
 * 2. Permission-Based Protection:
 *    - User is authenticated but lacks CASL permission
 *    - Example: guide trying to access /admin/users
 *    - Requires: action='manage', subject='users'
 *
 * RECOVERY OPTIONS:
 * ────────────────────────────────────────────────────────────────
 * - "Go Back" button: Returns to previous page
 * - "Go to Homepage" link: Safe fallback destination
 * - "Contact Support" email: Request permission escalation
 *
 * TODO: Enhance error page
 * - Show which permission/role is required
 * - Provide request access workflow
 * - Log unauthorized access attempts for security monitoring
 */

import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';

export default function UnauthorizedPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />

      <main className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full text-center">
          {/* Icon */}
          <div className="mx-auto w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mb-6">
            <svg className="w-12 h-12 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>

          {/* Title */}
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Access Denied
          </h1>

          {/* Message */}
          <p className="text-lg text-gray-600 mb-8">
            You don&apos;t have permission to access this page. Please contact an administrator if you believe this is an error.
          </p>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate(-1)}
              className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition font-medium"
            >
              Go Back
            </button>
            <Link
              to="/"
              className="px-6 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition font-medium"
            >
              Go to Homepage
            </Link>
          </div>

          {/* Support link */}
          <p className="mt-8 text-sm text-gray-500">
            Need help?{' '}
            <a href="mailto:support@outdoorguides.com" className="text-blue-600 hover:text-blue-700 underline">
              Contact Support
            </a>
          </p>
        </div>
      </main>

      <Footer />
    </div>
  );
}
